<template>
  <div>
    <section class="sli-sheet sli-sheet--title sli-sheet--inner">
      <div class="sli-list-head d-flex align-items-center justify-content-between">
        <h3 class="mb-0 text-white">
          الخرائط
        </h3>
        <button
          v-if="canCreate"
          type="button"
          class="sli-save sli-save--sm"
          @click="startCreate"
        >
          إضافة خريطة
        </button>
      </div>
    </section>

    <section
      v-if="showForm"
      class="sli-sheet sli-sheet--lands"
    >
      <header class="sli-sheet__head">
        {{ editingId ? 'تعديل خريطة' : 'خريطة جديدة' }}
      </header>
      <div class="sli-grid sli-grid--fit" style="padding: 8px 12px;">
        <div class="sli-cell sli-cell--fit" style="--sli-fit-ch: 10;">
          <label class="sli-cell__label">شمال / جنوب</label>
          <select
            v-model="form.north_south"
            class="sli-cell__input"
          >
            <option
              v-for="opt in nsOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.text }}
            </option>
          </select>
        </div>
        <div class="sli-cell sli-cell--fit" style="--sli-fit-ch: 10;">
          <label class="sli-cell__label">شرق / غرب</label>
          <select
            v-model="form.east_west"
            class="sli-cell__input"
          >
            <option
              v-for="opt in ewOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.text }}
            </option>
          </select>
        </div>
        <div class="sli-cell">
          <label class="sli-cell__label">رقم الخريطة</label>
          <div class="sli-digits">
            <input
              v-for="n in 3"
              :key="n"
              v-model="form[`digit_${n}`]"
              class="sli-cell__input sli-digit"
              inputmode="numeric"
              maxlength="1"
              @input="keepDigit(n, $event)"
            >
          </div>
        </div>
        <div class="sli-cell sli-span-2">
          <label class="sli-cell__label">ملف اللوحة (PDF أو TIFF)</label>
          <input
            type="file"
            accept=".pdf,.tif,.tiff,application/pdf,image/tiff"
            class="sli-file-input"
            @change="onSheet($event)"
          >
          <small v-if="form.sheet_file">{{ form.sheet_file.name }}</small>
        </div>
        <div class="sli-cell sli-span-2">
          <label class="sli-radios">
            <input
              v-model="form.is_returned"
              type="checkbox"
            >
            تم إرجاع الخريطة
          </label>
        </div>
        <template v-if="form.is_returned">
          <div class="sli-cell sli-span-2">
            <label class="sli-cell__label">TIFF المُرجع</label>
            <input
              type="file"
              accept=".tif,.tiff,image/tiff"
              class="sli-file-input"
              @change="onReturnFile('returned_tiff', $event)"
            >
            <small v-if="form.returned_tiff">{{ form.returned_tiff.name }}</small>
          </div>
          <div class="sli-cell sli-span-2">
            <label class="sli-cell__label">ZIP المُرجع</label>
            <input
              type="file"
              accept=".zip,application/zip"
              class="sli-file-input"
              @change="onReturnFile('returned_zip', $event)"
            >
            <small v-if="form.returned_zip">{{ form.returned_zip.name }}</small>
          </div>
        </template>
      </div>

      <header class="sli-sheet__head">
        قطع الخريطة
      </header>
      <div
        v-for="(parcel, index) in form.parcels"
        :key="parcel._key || index"
        class="sli-grid sli-grid--fit sli-enc-row"
        style="padding: 8px 12px;"
      >
        <sli-field
          v-model="parcel.basin_name"
          label="اسم الحوض"
          :input-id="`map-basin-${index}`"
          fit="14"
        />
        <sli-field
          v-model="parcel.basin_number"
          label="رقم الحوض"
          :input-id="`map-block-${index}`"
          inputmode="numeric"
          compact
        />
        <sli-field
          v-model="parcel.parcel_number"
          label="رقم القطعة"
          :input-id="`map-parcel-${index}`"
          inputmode="numeric"
          compact
        />
        <div class="sli-cell sli-cell--action">
          <button
            v-if="form.parcels.length > 1"
            type="button"
            class="sli-link-btn"
            @click="removeParcel(index)"
          >
            حذف
          </button>
        </div>
      </div>
      <div class="sli-add-wrap">
        <button
          type="button"
          class="sli-add-btn"
          @click="addParcel"
        >
          + إضافة قطعة خريطة
        </button>
      </div>
      <div class="sli-add-wrap">
        <button
          type="button"
          class="sli-save sli-save--sm"
          :disabled="saving"
          @click="save"
        >
          حفظ الخريطة
        </button>
        <button
          type="button"
          class="sli-add-btn"
          @click="showForm = false"
        >
          إلغاء
        </button>
      </div>
    </section>

    <div
      v-if="loading"
      class="text-center py-2"
    >
      <b-spinner variant="primary" />
    </div>
    <div
      v-else-if="!rows.length"
      class="sli-sheet"
      style="padding: 18px;"
    >
      لا توجد خرائط
    </div>
    <article
      v-for="(item, index) in rows"
      :key="item.id || index"
      class="sli-location sli-inv-card"
      :class="index % 2 === 0 ? 'is-navy' : 'is-lime'"
    >
      <div class="sli-inv-card__body">
        <h4 class="sli-inv-card__title">
          {{ mapDisplayName(item) }}
        </h4>
        <div class="sli-inv-card__badges">
          <span
            v-if="item.is_returned"
            class="sli-chip sli-chip--on"
          >تم الإرجاع</span>
          <span class="sli-chip">عدد القطع {{ item.parcels_count || (item.parcels && item.parcels.length) || 0 }}</span>
        </div>
        <div class="sli-inv-card__actions">
          <button
            v-if="canCreate"
            type="button"
            class="sli-text-link"
            @click="startEdit(item)"
          >
            تعديل
          </button>
        </div>
      </div>
    </article>
    <b-pagination
      v-if="meta && meta.total"
      v-model="page"
      align="center"
      :total-rows="meta.total"
      :per-page="meta.per_page || 15"
      @change="fetchList"
    />
  </div>
</template>

<script>
import { BSpinner, BPagination } from 'bootstrap-vue'
import seizureLandInventoriesApi from '@/api/seizure-land-inventories/seizureLandInventories'
import SliField from './SliField.vue'
import {
  unwrapList,
  emptyMapForm,
  emptyMapParcel,
  mapDisplayName,
  NORTH_SOUTH_OPTIONS,
  EAST_WEST_OPTIONS,
} from '../formModel'

export default {
  name: 'SliMapsPanel',
  components: {
    BSpinner, BPagination, SliField,
  },
  data() {
    return {
      loading: false,
      saving: false,
      showForm: false,
      editingId: null,
      rows: [],
      meta: {},
      page: 1,
      form: emptyMapForm(),
      nsOptions: NORTH_SOUTH_OPTIONS,
      ewOptions: EAST_WEST_OPTIONS,
    }
  },
  computed: {
    canCreate() {
      return this.hasPermission('create seizure_land_inventories')
        || this.hasPermission('edit seizure_land_inventories')
    },
  },
  created() {
    this.fetchList()
  },
  methods: {
    mapDisplayName,
    fetchList(page) {
      if (page) this.page = page
      this.loading = true
      seizureLandInventoriesApi.listMaps({ page: this.page, per_page: 15 })
        .then(res => {
          const parsed = unwrapList(res)
          this.rows = parsed.rows
          this.meta = parsed.meta || {}
        })
        .catch(() => {
          this.rows = []
          this.$toast.error('تعذر تحميل الخرائط')
        })
        .finally(() => {
          this.loading = false
        })
    },
    startCreate() {
      this.editingId = null
      this.form = emptyMapForm()
      this.showForm = true
    },
    startEdit(item) {
      const digits = String(item.map_number || item.number || '').replace(/\D/g, '').slice(0, 3)
      this.editingId = item.id
      this.form = {
        ...emptyMapForm(),
        north_south: item.north_south || 'south',
        east_west: item.east_west || 'east',
        digit_1: digits[0] || '',
        digit_2: digits[1] || '',
        digit_3: digits[2] || '',
        is_returned: !!item.is_returned,
        parcels: Array.isArray(item.parcels) && item.parcels.length
          ? item.parcels.map(p => ({
            ...emptyMapParcel(),
            basin_name: p.basin_name || '',
            basin_number: p.basin_number || p.block_number || '',
            parcel_number: p.parcel_number || p.land_number || '',
          }))
          : [emptyMapParcel()],
      }
      this.showForm = true
    },
    keepDigit(n, event) {
      const raw = String(event.target.value || '').replace(/\D/g, '').slice(0, 1)
      this.form[`digit_${n}`] = raw
    },
    onSheet(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return
      const name = (file.name || '').toLowerCase()
      if (!(name.endsWith('.pdf') || name.endsWith('.tif') || name.endsWith('.tiff'))) {
        this.$toast.error('الملف يجب أن يكون PDF أو TIFF')
        return
      }
      this.form.sheet_file = file
    },
    onReturnFile(key, event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return
      const name = (file.name || '').toLowerCase()
      if (key === 'returned_tiff' && !(name.endsWith('.tif') || name.endsWith('.tiff'))) {
        this.$toast.error('ملف الإرجاع يجب أن يكون TIFF')
        return
      }
      if (key === 'returned_zip' && !name.endsWith('.zip')) {
        this.$toast.error('ملف الإرجاع المضغوط يجب أن يكون ZIP')
        return
      }
      this.form[key] = file
    },
    addParcel() {
      this.form.parcels.push(emptyMapParcel())
    },
    removeParcel(index) {
      this.form.parcels.splice(index, 1)
    },
    buildFormData() {
      const fd = new FormData()
      fd.append('north_south', this.form.north_south)
      fd.append('east_west', this.form.east_west)
      fd.append('digit_1', this.form.digit_1)
      fd.append('digit_2', this.form.digit_2)
      fd.append('digit_3', this.form.digit_3)
      fd.append('is_returned', this.form.is_returned ? '1' : '0')
      if (this.form.sheet_file instanceof File) fd.append('sheet_file', this.form.sheet_file)
      if (this.form.is_returned && this.form.returned_tiff instanceof File) {
        fd.append('returned_tiff', this.form.returned_tiff)
      }
      if (this.form.is_returned && this.form.returned_zip instanceof File) {
        fd.append('returned_zip', this.form.returned_zip)
      }
      this.form.parcels.forEach((p, i) => {
        fd.append(`parcels[${i}][basin_name]`, p.basin_name || '')
        fd.append(`parcels[${i}][basin_number]`, p.basin_number || '')
        fd.append(`parcels[${i}][parcel_number]`, p.parcel_number || '')
      })
      return fd
    },
    save() {
      const digits = `${this.form.digit_1}${this.form.digit_2}${this.form.digit_3}`
      if (!this.form.north_south || !this.form.east_west || digits.length !== 3) {
        this.$toast.error('حدد الربع الجغرافي ورقم الخريطة من ثلاثة أرقام')
        return
      }
      if (this.form.is_returned && !(this.form.returned_tiff instanceof File && this.form.returned_zip instanceof File) && !this.editingId) {
        this.$toast.error('عند الإرجاع يلزم ملف TIFF وملف ZIP')
        return
      }
      if (this.form.is_returned && this.editingId && !(this.form.returned_tiff instanceof File && this.form.returned_zip instanceof File)) {
        this.$toast.error('عند الإرجاع يلزم ملف TIFF وملف ZIP')
        return
      }
      this.saving = true
      const fd = this.buildFormData()
      const req = this.editingId
        ? seizureLandInventoriesApi.updateMap(this.editingId, fd)
        : seizureLandInventoriesApi.storeMap(fd)
      req
        .then(() => {
          this.$toast.success('تم حفظ الخريطة')
          this.showForm = false
          this.fetchList()
        })
        .catch(err => {
          const status = err?.response?.status
          const message = err?.response?.data?.message || ''
          if (status === 422) {
            this.$toast.error(message || 'رقم الخريطة مستخدم في نفس الربع الجغرافي')
            return
          }
          this.$toast.error(message || 'تعذر حفظ الخريطة')
        })
        .finally(() => {
          this.saving = false
        })
    },
  },
}
</script>

<style scoped>
.sli-sheet--inner {
  margin-bottom: 12px;
}
.sli-digits {
  display: flex;
  gap: 6px;
}
.sli-digit {
  width: 2.4rem;
  text-align: center;
}
.sli-file-input {
  font-size: 0.8rem;
}
</style>
